import { useTranslation } from 'react-i18next'
import { Card, CardContent } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'

// The placeholder while a query loads, shaped like what replaces it so the page doesn't jump:
// `list` for rows in a card (a list page or a tab), `lines` for the same rows without the card
// (inside a card or dialog that is already there), `form` for label + field pairs, `page` for a
// whole screen that returns early, before its heading. Screen readers hear "Memuat…".
export function LoadingSkeleton({
  variant = 'list',
  rows = 5,
}: {
  variant?: 'list' | 'lines' | 'form' | 'page'
  rows?: number
}) {
  const { t } = useTranslation()
  return (
    <div role="status" aria-busy className="flex flex-col gap-4">
      <span className="sr-only">{t('app.loading')}</span>
      {variant === 'page' ? (
        <>
          <Skeleton className="h-4 w-24" />
          <Skeleton className="h-7 w-56" />
          <SkeletonRows rows={rows} />
        </>
      ) : variant === 'form' ? (
        Array.from({ length: rows }, (_, index) => (
          <div key={index} className="flex flex-col gap-2">
            <Skeleton className="h-4 w-28" />
            <Skeleton className="h-11 w-full rounded-lg" />
          </div>
        ))
      ) : variant === 'lines' ? (
        <SkeletonLines rows={rows} />
      ) : (
        <SkeletonRows rows={rows} />
      )}
    </div>
  )
}

function SkeletonRows({ rows }: { rows: number }) {
  return (
    <Card>
      <CardContent className="py-4">
        <SkeletonLines rows={rows} />
      </CardContent>
    </Card>
  )
}

function SkeletonLines({ rows }: { rows: number }) {
  return (
    <div className="flex flex-col gap-4">
      {Array.from({ length: rows }, (_, index) => (
        <div key={index} className="flex items-center gap-3">
          <div className="flex flex-1 flex-col gap-2">
            <Skeleton className="h-4 w-2/3" />
            <Skeleton className="h-3 w-1/3" />
          </div>
          <Skeleton className="h-4 w-16" />
        </div>
      ))}
    </div>
  )
}
